/* session-guard.js — THE single source of truth for "what kind of session is this?"
 *
 * Loads FIRST, before every other custom script, on all three stores. Everything
 * that writes to a real system asks this module for permission.
 *
 * WHY THIS EXISTS
 * ---------------
 * The old guard inferred "is this a test session?" from the SHAPE of the visitor
 * id (a `facade00-` prefix). That is a DENYLIST of known-fakes, so it fails OPEN
 * for the one dangerous case: a session browsing as a REAL customer is
 * byte-identical to that real customer, and nothing — client or server — could
 * tell. This module replaces inference with an EXPLICIT, tab-scoped marker.
 *
 * THE THREE RULES IT ENFORCES
 *   1. never mutate the real person's state
 *   2. never be counted as a real shopper
 *   3. never persist into the operator's own browsing
 *
 * SESSION KINDS (window.SE_Session.kind())
 *   'shopper'  — a real visitor. Everything behaves normally. THE DEFAULT.
 *   'internal' — a team member or a test identity. Recognisable server-side.
 *   'assumed'  — browsing as somebody else. The locked-down mode.
 *
 * WRITE POLICY — ALLOWLIST, FAILS CLOSED
 * A channel must be explicitly permitted for the current kind. A channel nobody
 * listed is DENIED, so a new writer added next year is safe by default rather
 * than silently inheriting zero protection.
 *
 * TAB-SCOPED BY CONSTRUCTION
 * State lives in sessionStorage only — never localStorage, never a cookie — so
 * closing the tab ends the session and it can never bleed into normal browsing.
 *
 * Self-contained by the isolation principle: no imports, no shared module, reads
 * only its own storage + window globals. Safe to load anywhere, any order.
 */
(function () {
  'use strict';

  if (window.SE_Session && window.SE_Session.__v) return;   // idempotent

  var KEY = 'AGB_SessionState';          // tab-scoped; innocuous name
  var TEST_PREFIX = 'facade00-';         // panel-minted test identity
  var TEAM_PREFIX = 'staff00-';          // internal team member

  function _read() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || typeof o !== 'object') return null;
      if (o.until && Date.now() > o.until) { _clear(); return null; }  // hard expiry
      return o;
    } catch (e) { return null; }
  }
  function _write(o) { try { sessionStorage.setItem(KEY, JSON.stringify(o)); } catch (e) {} }
  function _clear() { try { sessionStorage.removeItem(KEY); } catch (e) {} }

  function _vid() {
    try { return localStorage.getItem('visitorID') || localStorage.getItem('viewerId') || ''; }
    catch (e) { return ''; }
  }

  /* The session kind, resolved fresh on every call.
   * The explicit marker WINS over any id-shape reading — that is the whole
   * point. Prefix checks remain only as a fallback so an existing test/team
   * identity is still recognised when no marker was ever set. */
  function kind() {
    var s = _read();
    if (s && s.kind) return s.kind;
    var v = _vid();
    if (v.indexOf(TEST_PREFIX) === 0 || v.indexOf(TEAM_PREFIX) === 0) return 'internal';
    return 'shopper';
  }

  /* ── Write policy ────────────────────────────────────────────────────────
   * Per kind, the channels that MAY reach a real system. Anything absent is
   * denied. Channel names are the logical write, not the URL, so a moved
   * endpoint keeps its policy.
   *
   *   analytics      storefront event beacons (CAE / gear-builder / TTB)
   *   identity       email <-> visitor binding (users_drip and friends)
   *   cart-stamp     writing an identity into the Shopify cart / order
   *   subscribe      back-in-stock waitlist rows
   *   campaign       giveaway / games entries
   *   errors         error reports
   *   visitor-state  server-side visitor identify + state save
   */
  var POLICY = {
    // A real shopper: everything, exactly as today.
    shopper: { analytics: 1, identity: 1, 'cart-stamp': 1, subscribe: 1, campaign: 1, errors: 1, 'visitor-state': 1 },

    // A team member or test identity. The rule here is DO NOT REGRESS TODAY'S
    // BEHAVIOUR: nothing currently blocks a staff00-/facade00- browser from
    // these channels, and each one is already recognisable at rest by its id
    // prefix, so blocking it destroys data instead of protecting anyone.
    //   analytics/errors  keep flowing — tagged, so reporting can exclude them
    //   cart-stamp        MUST stay allowed: it is how a staff order is known
    //                     to BE a staff order. Deny it and staff purchases
    //                     become indistinguishable from shopper purchases in
    //                     every downstream report — the opposite of the goal.
    //   subscribe         MUST stay allowed: restock-notifier deliberately
    //                     permits staff on the waitlist (a dated, unit-tested
    //                     decision still written into the service).
    // Denied: identity, campaign, visitor-state — those create or mutate a
    // record keyed to a PERSON, and a test id has no person behind it.
    internal: { analytics: 1, errors: 1, 'cart-stamp': 1, subscribe: 1 },

    // Browsing as somebody else: nothing may leave. Not analytics (it would be
    // attributed to them), not errors (they carry identity), nothing.
    assumed: {}
  };

  function mayWrite(channel) {
    var allowed = POLICY[kind()] || POLICY.assumed;
    return !!allowed[channel];
  }

  // Blocked writes are recorded in-memory so the operator can see exactly what
  // WOULD have happened. Never persisted, capped so a loop can't grow it.
  var blocked = [];
  function _note(channel, detail) {
    try {
      if (blocked.length < 200) blocked.push({ at: Date.now(), channel: channel, detail: String(detail || '').slice(0, 200) });
      if (window.SE_Session.onBlocked) window.SE_Session.onBlocked(channel, detail);
    } catch (e) {}
  }

  /* The guard every writer calls. Returns true when the write may proceed.
   * `detail` is only for the operator-facing log. */
  function allow(channel, detail) {
    if (mayWrite(channel)) return true;
    _note(channel, detail);
    return false;
  }

  /* The marker to attach to any write that IS allowed, so the server can tag
   * the row at rest instead of guessing from an id prefix. Null for a real
   * shopper (nothing added to normal traffic). */
  function marker() {
    var k = kind();
    if (k === 'shopper') return null;
    var s = _read() || {};
    return { session_kind: k, session_ref: s.ref || null, theme_role: _themeRole() };
  }

  function _themeRole() {
    try {
      var r = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
      return r === 'main' ? 'live' : (r ? 'preview' : 'unknown');
    } catch (e) { return 'unknown'; }
  }

  function isPreview() { return _themeRole() !== 'live'; }

  /* Can end() actually put the operator back the way they were?
   *
   * ONLY if begin() ran and snapshotted their identity. Without a snapshot,
   * end() cannot restore anything — it can only DELETE, which for a session
   * that was merely RECOGNISED (a staff00- browser that was already itself)
   * means wiping the very staff flag that keeps their browsing out of the
   * shopper reports. They would have to re-flag from their team link.
   *
   * So any UI offering an "exit" must ask this FIRST and hide the control when
   * it is false: a button that can only destroy is worse than no button. */
  function canRestore() {
    var s = _read();
    return !!(s && s.prev && (s.prev.visitorID || s.prev.cookie));
  }

  /* ── Begin / end ─────────────────────────────────────────────────────────
   * begin() snapshots the operator's OWN identity first so end() can restore it
   * byte-for-byte. Deleting instead of restoring would mint a brand-new identity
   * (and a 300-day cookie), which is its own contamination.
   */
  function begin(kindWanted, opts) {
    opts = opts || {};
    var k = kindWanted === 'assumed' ? 'assumed' : 'internal';
    var prev = null;
    try {
      prev = {
        visitorID: localStorage.getItem('visitorID'),
        viewerId: localStorage.getItem('viewerId'),
        cookie: _readCookie('visitorID')
      };
    } catch (e) {}
    _write({
      kind: k,
      ref: opts.ref || null,                 // opaque server-minted reference
      startedAt: Date.now(),
      until: Date.now() + (opts.minutes || 60) * 60000,
      prev: prev
    });
    return k;
  }

  /* Full teardown. Restores the operator's own identity, clears everything the
   * session touched, and reloads so no already-booted script keeps stale state.
   * The cart un-stamp must run BEFORE the id is cleared (the stamper no-ops on
   * an empty id, which would strand the attribute on a real order). */
  function end(opts) {
    opts = opts || {};
    var s = _read();
    var prev = (s && s.prev) || null;

    // 1. un-stamp the cart while we still know what to remove
    var unstamp = null;
    try {
      unstamp = fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { visitorID: '', SE_SessionId: '' } })
      }).catch(function () {});
    } catch (e) {}

    function finish() {
      // 2. clear what the session wrote.
      //
      // HOW WIDE depends on whether we have a snapshot to restore FROM.
      //   prev != null  — begin() ran, so we know exactly what the operator's
      //                   own state was and can safely wipe wide, then put it
      //                   back. Promo/game keys written while impersonating
      //                   belong to nobody and must go.
      //   prev == null  — end() was called on a session we never opened (e.g. a
      //                   staff browser that was simply RECOGNISED by its id
      //                   prefix). Every key here is then the operator's OWN
      //                   real state — their ticking-time-bomb progress, games,
      //                   easter-egg clicks, chosen country. Wiping it would
      //                   destroy real data to solve nothing, so touch only
      //                   identity + this module's own keys.
      var WIDE = /^(AGB_|SE_|TTB_|ttb_|ag_games_|egg-clicked:|recommend|viewerId|visitorID|country|tande)/i;
      var NARROW = /^(AGB_Session|SE_Session|viewerId|visitorID)/i;
      var pattern = prev ? WIDE : NARROW;
      try {
        var kill = [];
        for (var i = 0; i < localStorage.length; i++) {
          var key = localStorage.key(i);
          if (key && pattern.test(key)) kill.push(key);
        }
        kill.forEach(function (key) { try { localStorage.removeItem(key); } catch (e) {} });
      } catch (e) {}
      try { sessionStorage.removeItem(KEY); } catch (e) {}

      // 3. restore the operator's OWN identity (never just delete).
      // The cookie MUST be dealt with in every branch: it is the authoritative
      // seed, so leaving a stale one means the next page load resurrects the
      // very identity we are trying to leave — "exit" that does not exit.
      try {
        if (prev && prev.visitorID) localStorage.setItem('visitorID', prev.visitorID);
        if (prev && prev.viewerId) localStorage.setItem('viewerId', prev.viewerId);
        if (prev && prev.cookie) _writeCookie('visitorID', prev.cookie, 300);
        else _deleteCookie('visitorID');
      } catch (e) {}

      _clear();
      // 4. reload — a booted engine still holds the old identity in memory
      if (opts.reload !== false) { try { location.reload(); } catch (e) {} }
    }

    if (unstamp && unstamp.then) unstamp.then(finish, finish); else finish();
    return true;
  }

  function _readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  function _writeCookie(name, val, days) {
    try {
      var d = new Date(); d.setTime(d.getTime() + days * 864e5);
      document.cookie = name + '=' + encodeURIComponent(val) + ';expires=' + d.toUTCString() + ';path=/';
    } catch (e) {}
  }
  function _deleteCookie(name) {
    try { document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/'; } catch (e) {}
  }

  window.SE_Session = {
    __v: 1,
    kind: kind,
    isShopper: function () { return kind() === 'shopper'; },
    isInternal: function () { return kind() !== 'shopper'; },
    isAssumed: function () { return kind() === 'assumed'; },
    mayWrite: mayWrite,
    allow: allow,
    marker: marker,
    blocked: function () { return blocked.slice(); },
    begin: begin,
    end: end,
    canRestore: canRestore,
    isPreview: isPreview,
    themeRole: _themeRole
  };

  /* BACK-COMPAT — deliberately PURELY ADDITIVE.
   *
   * Five existing files (cae-client, agb-analytics, ticking-time-bomb,
   * devlog-client, the engine) call SE_isInternalSession() and DROP their
   * analytics when it returns true. Its old meaning was exactly `facade00-`.
   *
   * It is tempting to write `kind() !== 'shopper'` here — DON'T. That newly
   * returns true for a `staff00-` browser, so staff analytics would stop being
   * emitted and the panel's "Internal team traffic" section — which counts
   * events WHERE visitor_id LIKE 'staff00-%' — would silently show ZERO for
   * every staff member. A working feature broken by a one-word change.
   *
   * So: preserve the old answer for every pre-existing case, and ONLY ADD the
   * one it could never express — an 'assumed' session, where emitting analytics
   * would attribute the operator's browsing to the real person. */
  try {
    window.SE_isInternalSession = function () {
      try {
        if (kind() === 'assumed') return true;          // NEW: closes the leak
        return (localStorage.getItem('visitorID') || '').indexOf(TEST_PREFIX) === 0;
      } catch (e) { return false; }                     // unchanged for staff + shoppers
    };
  } catch (e) {}
})();
